/**
 * The hall as an architect's drawing, in SVG: the walls, the curved stage, the side rooms and columns,
 * and over them what a layout covers. It is each layout's picture on the event page, and the faint
 * drawing behind the sign-in page.
 */

import type { MeasuredModel } from '@mawkeb/shared/model';
import type { PreviewShape } from '@mawkeb/shared/preview';
import { HALL_OUTLINE } from './hallOutline';

interface Outline {
  bounds: readonly [number, number, number, number];
  floor: readonly (readonly [number, number])[];
  solids: readonly { kind: string; outline: readonly (readonly [number, number])[] }[];
}

function outlineOf(model: MeasuredModel | null): Outline {
  if (!model) return HALL_OUTLINE;
  return {
    bounds: [model.bounds.min[0], model.bounds.min[1], model.bounds.max[0], model.bounds.max[1]],
    floor: model.floor.outline,
    solids: model.solids.map((s) => ({ kind: s.kind, outline: s.outline })),
  };
}

/** Hall y runs towards the stage; on the drawing the stage is at the top. */
const pts = (o: readonly (readonly [number, number])[], top: number) => o.map(([x, y]) => `${x.toFixed(2)},${(top - y).toFixed(2)}`).join(' ');

function shapePoints(p: number[], top: number): string {
  let s = '';
  for (let i = 0; i + 1 < p.length; i += 2) s += `${(p[i]! / 100).toFixed(2)},${(top - p[i + 1]! / 100).toFixed(2)} `;
  return s;
}

export function HallDrawing({ model, shapes, className, label }: { model: MeasuredModel | null; shapes?: PreviewShape[]; className?: string; label?: string }) {
  const o = outlineOf(model);
  const [x0, y0, x1, y1] = o.bounds;
  const pad = 0.6;
  const w = x1 - x0 + pad * 2;
  const h = y1 - y0 + pad * 2;
  // y is flipped about the hall's far end, so the viewBox starts at 0
  const top = y1 + pad;
  return (
    <svg className={`hall-drawing ${className ?? ''}`} viewBox={`${x0 - pad} 0 ${w} ${h}`} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true} preserveAspectRatio="xMidYMid meet">
      <polygon className="floor" points={pts(o.floor, top)} />
      {shapes?.map((s, i) =>
        s.k === 'person' ? (
          <circle key={i} className="person" cx={s.p[0]! / 100} cy={top - s.p[1]! / 100} r={0.22} />
        ) : (
          <polygon key={i} className={s.k} points={shapePoints(s.p, top)} />
        ),
      )}
      {o.solids.map((s, i) => (
        <polygon key={`s${i}`} className={`solid ${s.kind}`} points={pts(s.outline, top)} />
      ))}
      <polygon className="walls" points={pts(o.floor, top)} />
    </svg>
  );
}
